import { A, D, pipe } from "@mobily/ts-belt"
import type {
  Course,
  CourseCalendarDate,
  CourseCalendarDateCustom,
  Unit,
} from "@shared/models/course"
import { createCourseDateCustomAttributes, createUnit } from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar, SchoolDays } from "@shared/models/rotation-calendar"
import { isCourseDateCustom, isCourseDateDefault } from "cc-frontend/utils/filter-dates/course"
import {
  compact,
  concat,
  filter,
  first,
  includes,
  isNil,
  isString,
  keyBy,
  last,
  max,
  min,
  pull,
  size,
} from "lodash-es"
import type { TransferrableDate } from "@shared/models/transferrable-date"

function formatDateAsISO(date: Date) {
  return dateFns.format(date, "YYYY-MM-DD")
}

function without<T>(arr: Array<T>, ...values: Array<T>): Array<T> {
  return arr.reject((value) => values.includes(value))
}

function cloneDeep<T>(value: T): T {
  return JSON.parse(JSON.stringify(value))
}

function intersection<T>(arr1: Array<T>, arr2: Array<T>): Array<T> {
  return (arr1 || []).filter((value) => (arr2 || []).includes(value))
}

/**
 * Loops through all the rotation dates and toggles off any custom or default dates.
 * Then, we take out any dates being forced on.
 */
export function consolidateCourseDatesOff(
  course: Course,
  planbook: Planbook,
  rotationCalendar: RotationCalendar
) {
  let courseDatesOff = new Set(course.attributes.calendar.datesOff)
  let courseDatesForcedOn = new Set(course.attributes.calendar.datesForcedOn)
  let planbookDatesOff = new Set(planbook.attributes.calendar.datesOff)
  let rotationDates = rotationCalendar.attributes.dates

  let unitStartDates = new Set(A.map(course.attributes.calendar.units, (unit) => unit.startDate))
  let unitEndDates = new Set(A.map(course.attributes.calendar.units, (unit) => unit.endDate))

  // loop through rotationDates
  // - convert all the semester/rotationIds to find out if it's is on.
  // then, we only have to look to see if it's the dateString is off.

  return pipe(
    rotationDates,
    A.filter((date) => {
      let semesterRotationString = date.attributes.semesterId + ":" + date.attributes.rotationId
      let planbookDefaultIsOff = planbookDatesOff.has(semesterRotationString)
      let courseDefaultIsOff = courseDatesOff.has(semesterRotationString)
      let planbookCustomIsOff = planbookDatesOff.has(date.attributes.dateString)
      let courseCustomIsOff = courseDatesOff.has(date.attributes.dateString)
      let dateHasStartOfUnit = unitStartDates.has(date.attributes.dateString)
      let dateHasEndOfUnit = unitEndDates.has(date.attributes.dateString)

      let planbookIsOff = planbookCustomIsOff ? planbookCustomIsOff : planbookDefaultIsOff
      let courseIsOff = courseCustomIsOff ? courseCustomIsOff : courseDefaultIsOff
      let courseDateForcedOn = courseDatesForcedOn.has(date.attributes.dateString)

      let isOff = planbookIsOff || (courseIsOff && !courseDateForcedOn)

      return isOff && !dateHasStartOfUnit && !dateHasEndOfUnit
    }),
    A.map((rotationDate) => rotationDate.attributes.dateString)
  )
}

export function findNewDate(
  amount: number,
  oldStartDate: string,
  datesOff: Array<string>,
  schoolDays: SchoolDays
) {
  if (amount === 0) return oldStartDate
  let dateOperation = amount > 0 ? dateFns.addDays : dateFns.subDays
  let date = dateFns.parse(oldStartDate)
  let dateRange: Array<string> = []
  let iterations = 0
  let schoolDaySet = new Set(schoolDays)
  let datesOffSet = new Set(datesOff)

  while (dateRange.length < Math.abs(amount) && iterations < 500) {
    iterations++
    date = dateOperation(date, 1)
    if (!schoolDaySet.has(dateFns.getDay(date))) continue
    let dateString = formatDateAsISO(date)
    if (datesOffSet.has(dateString)) continue
    dateRange.push(dateString)
  }

  return last(dateRange)
}

// TODO If it's a lesson, we want to merge it if there's a unitStart -- we should pass that in.
export function insertRange(
  transferrableArray: Array<TransferrableDate>,
  newStartDate: string,
  amount: number,
  unitHash: Unit | null,
  courseDates: Array<CourseCalendarDate>,
  datesOff: Array<string>,
  schoolDays: SchoolDays,
  isLesson: boolean
): CourseCalendarDate[] {
  transferrableArray = transferrableArray.slice()
  let courseDateMap = new Map(
    Object.entries(keyBy(filter(courseDates, isCourseDateCustom), "attributes.dateString"))
  )
  let newCourseDates: Array<CourseCalendarDateCustom> = []
  let modifiedDates: Map<string, CourseCalendarDateCustom> = new Map()
  let date = dateFns.parse(newStartDate)
  let datesLaidDown = 0
  let iterations = 0
  let schoolDaysSet = new Set(schoolDays)
  let datesOffSet = new Set(datesOff)

  while (transferrableArray.length > 0 && iterations < 500) {
    iterations++

    let dateString = formatDateAsISO(date)

    if (!schoolDaysSet.has(dateFns.getDay(date))) {
      date = dateFns.addDays(date, 1)
      continue
    }

    let courseDate = courseDateMap.get(dateString)
      ? cloneDeep(courseDateMap.get(dateString))
      : undefined

    if (datesOffSet.has(dateString)) {
      if (courseDate && courseDate.attributes.cardStackId) {
        // To avoid reordering lessons, we need to also move this dangling lesson along,
        // without putting a lesson in this slot.
        transferrableArray.push({
          cardStackId: courseDate.attributes.cardStackId,
          unitStart: [],
          unitEnd: [],
        })
        courseDate.attributes.cardStackId = null
        modifiedDates.set(dateString, courseDate)
      }
      date = dateFns.addDays(date, 1)
      continue
    }

    //==================================================
    // Apply a date if a date doesn't exist
    //==================================================
    if (courseDate === undefined) {
      let dateToTransfer = transferrableArray.shift()
      if (dateToTransfer !== undefined) {
        newCourseDates.push({
          id: dateString,
          type: "course-date-custom",
          attributes: {
            dateString: dateString,
            cardStackId: dateToTransfer.cardStackId,
            unitStart: dateToTransfer.unitStart.slice(0),
            unitEnd: dateToTransfer.unitEnd.slice(0),
            unitIds: [],
            isForcedOn: false,
            time: null,
          },
        })
        date = dateFns.addDays(date, 1)
        datesLaidDown++
        continue
      }
    } else {
      let isDraggingOntoStartOfParentUnit =
        unitHash && intersection(courseDate.attributes.unitStart, unitHash.ancestorIds).length > 0

      //==================================================
      // Pluck and Reset Date (if needed)
      //==================================================
      let isApplyingFirstLesson = isLesson === true && datesLaidDown === 0
      let nextTransferrableDate = first(transferrableArray)
      if (
        courseDate !== undefined &&
        nextTransferrableDate !== undefined &&
        shouldPluckCourseDate(
          courseDate,
          amount,
          transferrableArray.length,
          isApplyingFirstLesson,
          nextTransferrableDate
        )
      ) {
        // ------------------------
        // Step 1: Set unitStarts
        // ------------------------
        let dateToTransferUnitStart: Array<string> = []
        let courseDateToModifyUnitStart: Array<string> = []

        // Note: The first condition is to see if we're at the beginning and we're at the beginning of one the
        // unit's parent units. In that case, we don't want to push back the parent unit
        if (datesLaidDown === 0 && isDraggingOntoStartOfParentUnit) {
          let childUnitIdsToBump = unitHash
            ? without(courseDate.attributes.unitStart, ...unitHash.ancestorIds)
            : []
          dateToTransferUnitStart = childUnitIdsToBump
          courseDateToModifyUnitStart = without(
            courseDate.attributes.unitStart,
            ...childUnitIdsToBump
          )
        } else if (isApplyingFirstLesson) {
          dateToTransferUnitStart = []
          courseDateToModifyUnitStart = courseDate.attributes.unitStart.slice(0)
        } else {
          dateToTransferUnitStart = courseDate.attributes.unitStart.slice(0)
          courseDateToModifyUnitStart = []
        }

        // ----------------------------------
        // Step 2: Push date into transferrable array
        // ----------------------------------
        transferrableArray.push({
          cardStackId: courseDate.attributes.cardStackId,
          unitStart: dateToTransferUnitStart,
          unitEnd: courseDate.attributes.unitEnd.slice(0),
        })

        // ----------------------------------
        // Step 3: Reset date
        // ----------------------------------
        courseDate.attributes.cardStackId = null
        courseDate.attributes.unitStart = courseDateToModifyUnitStart
        courseDate.attributes.unitEnd = []
      }

      //==================================================
      // Apply a date
      //==================================================

      //------------------------
      // Figure out new unit end
      //------------------------
      let dateToTransfer = transferrableArray.shift()
      let newUnitEnd: Array<string> = []
      if (dateToTransfer !== undefined) {
        if (
          courseDate &&
          dateToTransfer &&
          datesLaidDown === 0 &&
          isDraggingOntoStartOfParentUnit
        ) {
          newUnitEnd = intersection(
            courseDate.attributes.unitEnd,
            unitHash ? unitHash.ancestorIds : []
          ).concat(dateToTransfer.unitEnd)
        } else {
          newUnitEnd = courseDate.attributes.unitEnd.concat(dateToTransfer.unitEnd)
        }

        //------------------------
        // Set properties on course date
        //------------------------
        courseDate.attributes.cardStackId = dateToTransfer.cardStackId
        courseDate.attributes.unitStart = courseDate.attributes.unitStart.concat(
          dateToTransfer.unitStart
        )
      }
      // courseDate.attributes.unitEnd = courseDate.attributes.unitEnd.concat(dateToTransfer.unitEnd)
      courseDate.attributes.unitEnd = newUnitEnd
      courseDate.attributes.unitIds = [] // reset it

      //----------------------
      // Overwrite old courseDate
      //----------------------
      modifiedDates.set(dateString, courseDate)

      //-------------------
      // Increment counters
      //-------------------
      datesLaidDown++
      date = dateFns.addDays(date, 1)
    }
  }

  return A.map(courseDates, (cd) =>
    isCourseDateCustom(cd) ? modifiedDates.get(cd.attributes.dateString) || cd : cd
  ).concat(newCourseDates)
}

//  We use this when we just want to throw a range in and we're not really merging. For instance,
//  PULL_LESSON_BACKWARDS
export function simpleInsertRange(
  transferrableArray: Array<TransferrableDate>,
  newStartDate: string,
  courseDates: Array<CourseCalendarDate>,
  datesOff: Array<string>,
  schoolDays: SchoolDays
) {
  let courseDateMap = new Map(
    Object.entries(keyBy(filter(courseDates, isCourseDateCustom), "attributes.dateString"))
  )
  let newCourseDates: Array<CourseCalendarDateCustom> = []
  let modifiedDates: Map<string, CourseCalendarDate> = new Map()
  let date = dateFns.parse(newStartDate)
  let datesLaidDown = 0
  let iterations = 0

  while (transferrableArray.length > 0 && iterations < 500) {
    iterations++

    let dateString = formatDateAsISO(date)

    if (!A.includes(schoolDays, dateFns.getDay(date))) {
      date = dateFns.addDays(date, 1)
      continue
    }
    if (A.includes(datesOff, dateString)) {
      date = dateFns.addDays(date, 1)
      continue
    }

    let courseDate = courseDateMap.get(dateString)
      ? cloneDeep(courseDateMap.get(dateString))
      : undefined

    //==================================================
    // Apply a date if a date doesn't exist
    //==================================================
    if (courseDate === undefined) {
      let dateToTransfer = transferrableArray.shift()
      if (dateToTransfer !== undefined) {
        newCourseDates.push({
          id: dateString,
          type: "course-date-custom",
          attributes: createCourseDateCustomAttributes({
            dateString: dateString,
            cardStackId: dateToTransfer.cardStackId,
            unitStart: dateToTransfer.unitStart.slice(0),
            unitEnd: dateToTransfer.unitEnd.slice(0),
            unitIds: [],
            isForcedOn: false,
            time: null,
          }),
        })
      }
      date = dateFns.addDays(date, 1)
      datesLaidDown++
      continue
    }

    //------------------------
    // Set properties on course date
    //------------------------
    let dateToTransfer = transferrableArray.shift()
    if (dateToTransfer !== undefined) {
      courseDate.attributes.cardStackId = dateToTransfer.cardStackId
      courseDate.attributes.unitStart = courseDate.attributes.unitStart.concat(
        dateToTransfer.unitStart
      )
      // courseDate.attributes.unitEnd = courseDate.attributes.unitEnd.concat(dateToTransfer.unitEnd)
      courseDate.attributes.unitEnd = dateToTransfer.unitEnd
      courseDate.attributes.unitIds = [] // reset it
    }

    //----------------------
    // Overwrite old courseDate
    //----------------------
    modifiedDates.set(dateString, courseDate)

    //-------------------
    // Increment counters
    //-------------------
    datesLaidDown++
    date = dateFns.addDays(date, 1)
  }
  return A.map(courseDates, (cd) =>
    isCourseDateCustom(cd) ? modifiedDates.get(cd.attributes.dateString) || cd : cd
  ).concat(newCourseDates)
}

export function moveEndOfUnit(
  amount: number,
  newEndDate: string,
  unitHash: Unit,
  courseDates: Array<CourseCalendarDate>,
  units: Array<Unit>,
  datesOff: Array<string>,
  schoolDays: SchoolDays
) {
  if (amount === 0) return courseDates

  // -------------------------------------------------------
  // Take the unit id out of the unitEnd
  // -------------------------------------------------------
  let courseDatesWithoutUnitEnd = A.map(A.filter(courseDates, isCourseDateCustom), (courseDate) => {
    if (A.includes(courseDate.attributes.unitEnd, unitHash.id)) {
      let newCourseDate = cloneDeep(courseDate)
      pull(newCourseDate.attributes.unitEnd, unitHash.id)
      return newCourseDate
    } else {
      return courseDate
    }
  })

  let courseDateMap = new Map(
    Object.entries(keyBy(courseDatesWithoutUnitEnd, "attributes.dateString"))
  )
  let newCourseDates: Array<CourseCalendarDateCustom> = []
  let modifiedDates: Map<string, CourseCalendarDateCustom> = new Map()
  let defaultCourseDates = A.filter(courseDates, isCourseDateDefault)
  let date = dateFns.parse(newEndDate)
  let iterations = 0
  let isFinding = true

  while (isFinding && iterations < 500) {
    iterations++

    let dateString = formatDateAsISO(date)

    // -------------------------------------------------------
    // Move to the next date if the day isn't in session
    // -------------------------------------------------------
    if (!A.includes(schoolDays, dateFns.getDay(date))) {
      date = amount > 0 ? dateFns.addDays(date, 1) : dateFns.subDays(date, 1)
      continue
    }
    if (A.includes(datesOff, dateString)) {
      date = amount > 0 ? dateFns.addDays(date, 1) : dateFns.subDays(date, 1)
      continue
    }

    // -------------------------------------------------------
    // Find the course date (if it exists)
    // -------------------------------------------------------
    let courseDate = courseDateMap.get(dateString) ? courseDateMap.get(dateString) : undefined

    // -------------------------------------------------------
    // CASE 2: We have a date bigger or less than the max or min
    // So we reset it
    // -------------------------------------------------------

    // Case 2a. See if it exists max date
    // -------------------------------------------------------
    let maxDate = pipe(
      units,
      A.filter((unit) => includes(unitHash.ancestorIds, unit.id)),
      A.map((unit) => unit.endDate),
      (endDates) => min(endDates)
    )

    if (unitHash.ancestorIds && maxDate && dateString > maxDate) {
      date = dateFns.parse(maxDate)
      continue
    }

    // Case 2b. See if it exists min date
    // -------------------------------------------------------
    let minDate = unitHash.startDate
    if (dateString < minDate) {
      date = dateFns.parse(minDate)
      continue
    }

    // CASE 2c: We have a date with the same bounds as it's parent
    // This is a bit confusing -- we look to see if it has the same start/endDate
    // as it's nearest parent. We tax the highest startDate of it's ancestors
    // and then check if we're at the max date and, if so, decrement the enddate
    // so it doesn't have the same start/end as it's parent
    // -------------------------------------------------------
    let parentUnitStartDate = pipe(
      units,
      A.filter((unit) => includes(unitHash.ancestorIds, unit.id)),
      A.map((unit) => unit.startDate),
      (dates) => max(dates)
    )

    if (unitHash.startDate === parentUnitStartDate && maxDate === dateString) {
      date = dateFns.subDays(date, 1)
      continue
    }

    // CASE 2d: We have a date with the same bounds as it's child
    // -------------------------------------------------------
    let childUnitsWithSameStart = filter(units, (unit) => {
      return includes(unitHash.descendantIds, unit.id) && unit.startDate === unitHash.startDate
    })
    let childUnitsWithSameStartIds = A.map(childUnitsWithSameStart, (unit) => unit.id)
    let childUnitEndDates = A.map(childUnitsWithSameStart, (unit) => unit.endDate)

    if (A.includes(childUnitEndDates, dateString)) {
      date = dateFns.subDays(date, 1) // This is hacky -- we actually need to iterate to make sure it's not a day off or not a school day
      continue
    }

    // -------------------------------------------------------
    // CASE 1: We don't have a course date
    // So we just apply it.
    // -------------------------------------------------------
    if (courseDate === undefined) {
      newCourseDates.push({
        id: dateString,
        type: "course-date-custom",
        attributes: {
          dateString: dateString,
          unitEnd: [unitHash.id],
          unitStart: [],
          unitIds: [],
          isForcedOn: false,
          cardStackId: null,
          time: null,
        },
      })
      isFinding = false
      continue
    } else {
      // -------------------------------------------------------
      // CASE 3: We have a unit there
      // -------------------------------------------------------
      let currentUnits = filter(courseDate.attributes.unitIds, (id) => {
        return (
          includes(unitHash.ancestorIds, id) === false &&
          includes(courseDate.attributes.unitEnd, id) === false &&
          unitHash.id !== id &&
          includes(childUnitsWithSameStartIds, id) !== true
        )
      })

      // CASE 3A: we're moving forward
      // So, we look for the end of the unit we dragged it into
      // -------------------------------------------------------
      if (currentUnits.length > 0 && amount > 0) {
        date = pipe(
          courseDate.attributes.unitIds,
          (arr) => without(arr, unitHash.id),
          A.map((id) =>
            A.find(
              units,
              (unit) => unit.id === id && unit.ancestorIds.length === unitHash.ancestorIds.length
            )
          ),
          (arr) => compact(arr),
          A.map((unit) => unit.endDate),
          (arr) => max(arr),
          (endDate) => dateFns.parse(endDate)
        )
        continue
      }

      // CASE 3B: we're moving backwards
      // So, we look for the start of the unit we dragged it into
      // ----------------------------
      if (currentUnits.length > 0 && amount < 0) {
        date = pipe(
          courseDate.attributes.unitIds,
          (ids) => without(ids, unitHash.id),
          A.map((id) =>
            A.find(
              units,
              (unit) => unit.id === id && unit.ancestorIds.length >= unitHash.ancestorIds.length
            )
          ),
          (arr) => compact(arr),
          A.map((unit) => unit.startDate),
          (dates) => min(dates),
          (date) => dateFns.parse(date),
          (date) => dateFns.subDays(date, 1) // This is hacky -- we actually need to iterate to make sure it's not a day off or not a school day
        )
        continue
      }

      // -------------------------------------------------------
      // CASE 4: We have a course date AND we're not in a unit
      // So we just apply it
      // -------------------------------------------------------
      let cloned = cloneDeep(courseDate)
      cloned.attributes.unitEnd.unshift(unitHash.id)
      modifiedDates.set(dateString, cloned)

      isFinding = false
      continue
    }
  }

  return pipe(
    courseDatesWithoutUnitEnd,
    A.map((cd) => modifiedDates.get(cd.attributes.dateString) || cd),
    A.concat(newCourseDates),
    A.concat<CourseCalendarDate>(defaultCourseDates)
  )
}
export function moveStartOfUnit(
  amount: number,
  newStartDate: string,
  unitHash: Unit,
  courseDates: Array<CourseCalendarDate>,
  units: Array<Unit>,
  datesOff: Array<string>,
  schoolDays: SchoolDays
) {
  if (amount === 0) return courseDates
  let defaultCourseDates = A.filter(courseDates, isCourseDateDefault)

  // -------------------------------------------------------
  // Take the unit id out of the unitStart
  // -------------------------------------------------------
  let courseDatesWithoutUnitStart = A.map(
    A.filter(courseDates, isCourseDateCustom),
    (courseDate) => {
      if (A.includes(courseDate.attributes.unitStart, unitHash.id)) {
        let newCourseDate = cloneDeep(courseDate)
        pull(newCourseDate.attributes.unitStart, unitHash.id)
        return newCourseDate
      } else {
        return courseDate
      }
    }
  )

  let courseDateMap = new Map(
    Object.entries(keyBy(courseDatesWithoutUnitStart, "attributes.dateString"))
  )
  let newCourseDates: Array<CourseCalendarDateCustom> = []
  let modifiedDates: Map<string, CourseCalendarDateCustom> = new Map()
  let date = dateFns.parse(newStartDate)
  let iterations = 0
  let isFinding = true

  while (isFinding && iterations < 500) {
    if (iterations === 499) {
      // console.log("TOO MANY ITERATIONS")
    }
    iterations++

    let dateString = formatDateAsISO(date)

    // -------------------------------------------------------
    // Move to the next date if the day isn't in session
    // -------------------------------------------------------
    if (!A.includes(schoolDays, dateFns.getDay(date))) {
      date = amount > 0 ? dateFns.addDays(date, 1) : dateFns.subDays(date, 1)
      // console.log("not a school day")
      continue
    }
    if (A.includes(datesOff, dateString)) {
      date = amount > 0 ? dateFns.addDays(date, 1) : dateFns.subDays(date, 1)
      // console.log("date off")
      continue
    }

    // -------------------------------------------------------
    // Find the course date (if it exists)
    // -------------------------------------------------------
    let courseDate = courseDateMap.get(dateString) ? courseDateMap.get(dateString) : undefined

    // -------------------------------------------------------
    // CASE 2: We have a date bigger or less than the max or min
    // of the parents So we reset it
    // -------------------------------------------------------

    // Case 2a. See if there's an ancestor with a shorter start date
    // -------------------------------------------------------
    let minDate = pipe(
      units,
      A.filter((unit) => includes(unitHash.ancestorIds, unit.id)),
      A.map((unit) => unit.startDate),
      (dates) => min(dates)
    )

    if (minDate && unitHash.ancestorIds && dateString < minDate) {
      date = dateFns.parse(minDate)
      // console.log("case 2a: see if there's an ancestor with a shorter start date")
      continue
    }

    // Case 2b. See if we're dragging past the end of the unit
    // -------------------------------------------------------
    let maxDate = unitHash.endDate
    if (dateString > maxDate) {
      date = dateFns.parse(maxDate)
      // console.log("Case 2b: We're dragging past the end of the unit")
      continue
    }

    // CASE 2c: We have a date with the same bounds as it's parent
    // This is a bit confusing -- we look to see if it has the same start/endDate
    // as it's nearest parent. We tax the highest startDate of it's ancestors
    // and then check if we're at the max date and, if so, decrement the enddate
    // so it doesn't have the same start/end as it's parent
    // -------------------------------------------------------
    let parentUnitStartDate = pipe(
      units,
      A.filter((unit) => includes(unitHash.ancestorIds, unit.id)),
      A.map((unit) => unit.startDate),
      (dates) => max(dates)
    )

    if (unitHash.startDate === parentUnitStartDate && maxDate === dateString) {
      date = dateFns.addDays(date, 1)
      // console.log("Case 2c: We have a date with the same bounds as it's parent")
      continue
    }

    // CASE 2d: We have a date with the same bounds as it's child
    // -------------------------------------------------------
    let childUnitsWithSameStart = filter(units, (unit) => {
      return includes(unitHash.descendantIds, unit.id) && unit.startDate === unitHash.startDate
    })
    let childUnitsWithSameStartIds = A.map(childUnitsWithSameStart, (unit) => unit.id)
    let childUnitStartDates = A.map(childUnitsWithSameStart, (unit) => unit.startDate)

    if (A.includes(childUnitStartDates, dateString)) {
      date = dateFns.addDays(date, 1) // This is hacky -- we actually need to iterate to make sure it's not a day off or not a school day
      // console.log("case 2d: We have a date with the same bounds as it's child")
      continue
    }

    // -------------------------------------------------------
    // CASE 1: We don't have a course date
    // So we just apply it.
    // -------------------------------------------------------
    if (courseDate === undefined) {
      // console.log("CASE 1: We don't have a course date")
      newCourseDates.push({
        id: dateString,
        type: "course-date-custom",
        attributes: createCourseDateCustomAttributes({
          dateString: dateString,
          unitStart: [unitHash.id],
        }),
      })
      isFinding = false
      continue
    }

    // -------------------------------------------------------
    // CASE 3: We have a unit there
    // -------------------------------------------------------
    let currentUnits = filter(courseDate.attributes.unitIds, (id) => {
      return (
        includes(unitHash.ancestorIds, id) === false &&
        includes(courseDate.attributes.unitStart, id) === false &&
        unitHash.id !== id &&
        includes(childUnitsWithSameStartIds, id) !== true
      )
    })

    // CASE 3A: we're moving backwards
    // So, we look for the start of the unit we dragged it into
    // -------------------------------------------------------
    if (currentUnits.length > 0 && amount < 0) {
      date = pipe(
        courseDate.attributes.unitIds,
        (arr) => without(arr, unitHash.id),
        A.map((id) =>
          A.find(
            units,
            (unit) => unit.id === id && unit.ancestorIds.length === unitHash.ancestorIds.length
          )
        ),
        (arr) => compact(arr),
        A.map((unit) => unit.startDate),
        (arr) => min(arr),
        (startDate) => dateFns.parse(startDate)
      )
      // console.log("case 3a: we're moving backwards")
      continue
    }

    // CASE 3B: we're moving forwards
    // So, we look for the end of the unit we dragged it into
    // ----------------------------
    if (currentUnits.length > 0 && amount > 0) {
      // console.log("CASE 3b: we're moving forwards", currentUnits, date)
      date = pipe(
        courseDate.attributes.unitIds,
        (arr) => without(arr, unitHash.id),
        A.map((id) =>
          A.find(
            units,
            (unit) => unit.id === id && unit.ancestorIds.length >= unitHash.ancestorIds.length
          )
        ),
        (arr) => compact(arr),
        A.map((unit) => unit.endDate),
        (arr) => min(arr),
        (date) => dateFns.parse(date),
        (date) => dateFns.addDays(date, 1) // This is hacky -- we actually need to iterate to make sure it's not a day off or not a school day
      )
      // console.log("advanced date to", date)
      // isFinding = false
      continue
    }

    // -------------------------------------------------------
    // CASE 4: We have a course date AND we're not in a unit
    // So we just apply it
    // -------------------------------------------------------
    // console.log("CASE 4: We have a course date AND we're not in a unit")
    // console.log("dateString", dateString, courseDate)
    let cloned = cloneDeep(courseDate)
    cloned.attributes.unitStart.unshift(unitHash.id)
    modifiedDates.set(dateString, cloned)

    isFinding = false
    continue
  }

  // console.log("update modified dates", modifiedDates)
  // console.log(_.map(modifiedDates, (d) => d.attributes.dateString))
  return pipe(
    courseDatesWithoutUnitStart,
    A.map((cd) => modifiedDates.get(cd.attributes.dateString) || cd),
    A.concat(newCourseDates),
    A.concat<CourseCalendarDate>(defaultCourseDates)
  )
}

/**
 */
export function constructUnitsAndDates(
  allCourseDates: Array<CourseCalendarDate>,
  datesOff: Array<string>,
  schoolDays: SchoolDays
): { courseDates: Array<CourseCalendarDate>; units: Array<Unit> } {
  // loop through dates and find min/max
  // add through, jumping over dates that are off and don't have unitStart/unitEnd
  // reduce to create the unit map
  // annotate the units with the ancestor/descendant ids
  // annotate the dates with unit ids

  let defaultCourseDates = A.filter(allCourseDates, isCourseDateDefault)
  let customCourseDates = A.sortBy(
    filter(allCourseDates, isCourseDateCustom),
    (date) => date.attributes.dateString
  )

  let courseDateStrings = A.map(customCourseDates, (date) => date.attributes.dateString)

  let range = createRange(
    // TS is not smart enough to realize undefined is only returned for empty arrays and this won't be empty
    min(courseDateStrings)!,
    // TS is not smart enough to realize undefined is only returned for empty arrays and this won't be empty
    max(courseDateStrings)!,
    customCourseDates,
    datesOff,
    schoolDays
  )

  // Create a unit for each unitId that exists.
  const allUnitIds = new Set<string>()
  for (const courseDate of range) {
    for (const unitId of courseDate.attributes.unitIds) {
      allUnitIds.add(unitId)
    }
    for (const unitId of courseDate.attributes.unitStart) {
      allUnitIds.add(unitId)
    }
    for (const unitId of courseDate.attributes.unitEnd) {
      allUnitIds.add(unitId)
    }
  }
  const unitsById = new Map<string, Unit>()
  for (const unitId of allUnitIds) {
    unitsById.set(unitId, createUnit({ id: unitId }))
  }

  // Fill in units' start and end dates.
  // If we can't find an explicit unitStart/unitEnd entry (can happen when units start/end
  // on a day off or due to various bugs), use the first/last day with that unitId.
  for (let i = 0; i < range.length; i++) {
    const courseDate = range[i]
    for (const unitId of courseDate.attributes.unitStart) {
      const unit = unitsById.get(unitId)!
      unit.startDate = courseDate.attributes.dateString
      unit.startIndex = i
    }
    for (const unitId of courseDate.attributes.unitIds) {
      const unit = unitsById.get(unitId)!
      if (!unit.startDate) {
        unit.startDate = courseDate.attributes.dateString
        unit.startIndex = i
      }
    }
  }
  for (let i = range.length - 1; i >= 0; i--) {
    const courseDate = range[i]
    for (const unitId of courseDate.attributes.unitEnd) {
      const unit = unitsById.get(unitId)!
      unit.endDate = courseDate.attributes.dateString
      unit.endIndex = i
    }
    for (const unitId of courseDate.attributes.unitIds) {
      const unit = unitsById.get(unitId)!
      if (!unit.endDate) {
        unit.endDate = courseDate.attributes.dateString
        unit.endIndex = i
      }
    }
  }

  // Fill in units' lengths and datesStrings.
  for (const unit of unitsById.values()) {
    unit.unitLength = unit.endIndex + 1 - unit.startIndex
    unit.dateStrings = pipe(
      range,
      A.filter(
        (cd) =>
          cd.attributes.dateString >= unit.startDate && cd.attributes.dateString <= unit.endDate
      ),
      A.map((cd) => cd.attributes.dateString)
    )
  }

  // Fill in course dates' unitIds, unitStart, and unitEnd.
  const newCourseDatesMap = new Map<string, CourseCalendarDateCustom>()
  for (const courseDate of range) {
    courseDate.attributes.unitStart = []
    courseDate.attributes.unitEnd = []
    courseDate.attributes.unitIds = []

    for (const unit of unitsById.values()) {
      if (unit.startDate === courseDate.attributes.dateString) {
        courseDate.attributes.unitStart.push(unit.id)
      }
      if (unit.endDate === courseDate.attributes.dateString) {
        courseDate.attributes.unitEnd.push(unit.id)
      }
      if (
        unit.startDate <= courseDate.attributes.dateString &&
        courseDate.attributes.dateString <= unit.endDate
      ) {
        courseDate.attributes.unitIds.push(unit.id)
      }
    }

    newCourseDatesMap.set(courseDate.attributes.dateString, courseDate)
  }

  // Fill in units' ancestor and descendant ids.
  let sortedUnits = A.sortBy(
    Array.from(unitsById.values()),
    (unit) => `${unit.startDate}:${unit.unitLength}`
  )
  A.forEach(sortedUnits, (unit) => {
    A.forEach(sortedUnits, (unit2) => {
      // The happy path
      if (unit2.endDate < unit.startDate && unit2.startDate < unit.startDate) return
      // if (unit2.startDate <=  unit.startDate && unit2.endDate >= unit.endDate && unit2.id !== unit.id && unit.startDate !== unit2.startDate && unit.endDate !== unit2.endDate){
      if (
        unit2.startDate <= unit.startDate &&
        unit2.endDate >= unit.endDate &&
        unit2.id !== unit.id &&
        !includes(unit2.ancestorIds, unit.id)
      ) {
        unit.ancestorIds.push(unit2.id)
      } else if (
        unit2.startDate >= unit.startDate &&
        unit2.endDate <= unit.endDate &&
        unit2.id !== unit.id
      ) {
        unit.descendantIds.push(unit2.id)
      } else if (
        unit2.id !== unit.id &&
        unit2.startDate === unit.startDate &&
        unit2.endDate === unit.endDate
      ) {
        console.log("Units have the same dates")
      } else if (
        unit2.startDate <= unit.startDate &&
        unit2.endDate >= unit.startDate &&
        unit2.endDate < unit.endDate &&
        unit2.id !== unit.id
      ) {
        console.log(`unit1: ${unit.id}: ${unit.startDate} - ${unit.endDate}`)
        console.log(`unit2: ${unit2.id}: ${unit2.startDate} - ${unit2.endDate}`)

        throw Error("Units overlap: starts before and breaks in the middle")
      } else if (
        unit2.startDate >= unit.startDate &&
        unit2.startDate <= unit.endDate &&
        unit2.endDate > unit.endDate &&
        unit2.id !== unit.id
      ) {
        console.log(`unit1: ${unit.id}: ${unit.startDate} - ${unit.endDate}`)
        console.log(`unit2: ${unit2.id}: ${unit2.startDate} - ${unit2.endDate}`)
        throw Error("Units overlap: starts after and extends past")
      }
    })
  })

  // Assemble complete course dates array.
  let oldCourseDatesMap = A.reduce(
    customCourseDates,
    new Map<string, CourseCalendarDateCustom>(),
    (acc, cd) => acc.set(cd.attributes.dateString, cd)
  )
  let courseDatesUpdated = A.map(
    customCourseDates,
    (cd) => newCourseDatesMap.get(cd.attributes.dateString) || cd
  )
  let addedCourseDates = A.filter(Array.from(newCourseDatesMap.values()), (courseDate) => {
    return oldCourseDatesMap.get(courseDate.attributes.dateString) === undefined
  })

  let finishedCourseDates = pipe(
    courseDatesUpdated,
    A.concat(addedCourseDates),
    A.filter((date) => isCourseDateWorthSaving(date)),
    A.sortBy((cd) => cd.attributes.dateString)
  )

  verifyCourseDates(finishedCourseDates)

  return {
    courseDates: concat<CourseCalendarDate>(finishedCourseDates, defaultCourseDates),
    units: Array.from(unitsById.values()),
  }
}

/**
 * Returns an array with a CourseCalendarDateCustom for each school day in the range
 * that is either on or has a unit.
 * Missing dates (default only) are created as needed, though missing unitIds.
 */
export function createRange(
  startDate: string,
  endDate: string,
  courseDates: Array<CourseCalendarDate>,
  datesOff: Array<string>,
  schoolDays: SchoolDays
) {
  let courseDateMap = new Map(
    Object.entries(keyBy(filter(courseDates, isCourseDateCustom), "attributes.dateString"))
  )

  let dateRange: Array<Date> = dateFns.eachDay(dateFns.parse(startDate), dateFns.parse(endDate))

  let schoolDaysSet = new Set(schoolDays)
  let datesOffSet = new Set(datesOff)

  return pipe(
    dateRange,
    A.filter((date) => schoolDaysSet.has(dateFns.getDay(date))), // filter for school days
    A.map((date) => formatDateAsISO(date)),
    A.map((dateString): CourseCalendarDateCustom => {
      return (
        courseDateMap.get(dateString) || {
          id: dateString,
          type: "course-date-custom",
          attributes: createCourseDateCustomAttributes({
            dateString: dateString,
          }),
        }
      )
    }),
    A.reject((courseDate) => {
      // If there's a unit, we can't skip it -- otherwise, our array of dates will be missing a unit bookend
      return (
        datesOffSet.has(courseDate.attributes.dateString) &&
        size(courseDate.attributes.unitStart) === 0 &&
        size(courseDate.attributes.unitEnd) === 0 &&
        size(courseDate.attributes.unitIds) === 0
      )
    })
  )
}

// the moving forward is more complex b/c we don't want to have a blank lesson
// at the end of the unit. So, if it doesn't have a lesson, we don't bump it.
// or, if we have a bunch more to bump, we want to push it out so we have space.
// This last condition is definitely the most confusing and hard for me to think about
function shouldPluckCourseDate(
  date: CourseCalendarDateCustom,
  amount: number,
  bufferLength: number,
  isApplyingFirstLesson: boolean,
  dateToInsert: TransferrableDate
) {
  if (isString(date.attributes.cardStackId)) return true

  let dateIsNotInAUnit = size(date.attributes.unitIds) === 0

  if (isApplyingFirstLesson && date.attributes.cardStackId === null && dateIsNotInAUnit)
    return false

  // If we're moving the end of a unit, we need to bump the beginning of the next unit
  if (size(dateToInsert.unitEnd) > 0 && size(date.attributes.unitStart) > 0) return true

  // If we only have one date to insert, we do it (given the conditions above haven't been met)
  if (bufferLength === 1) return false

  if (amount < 0) {
    return (
      size(date.attributes.unitStart) > 0 ||
      size(date.attributes.unitEnd) > 0 ||
      size(date.attributes.unitIds) > 0
    )
  } else {
    return (
      size(date.attributes.unitIds) > 0 || // there's a unit
      size(date.attributes.unitStart) > 0 || // it has a unit start
      (size(date.attributes.unitEnd) > 0 && date.attributes.cardStackId) || // there's a unitEnd AND a lesson
      (size(date.attributes.unitEnd) > 0 && bufferLength > 1) // there's a unitEnd AND we still have more things to move
    )
  }
}

export function getCardStackIdForDate(dates: Array<CourseCalendarDate>, dateString: string) {
  return pipe(
    dates,
    A.filter(isCourseDateCustom),
    A.find((date) => date.attributes.dateString === dateString),
    (date) => (date ? date.attributes.cardStackId : null)
  )
}

export function getCourseDateForCardStackId(dates: Array<CourseCalendarDate>, cardStackId: string) {
  return pipe(
    dates,
    A.filter(isCourseDateCustom),
    A.find((date) => date.attributes.cardStackId === cardStackId),
    (date) => date || null
  )
}

function isCourseDateWorthSaving(courseDate: CourseCalendarDateCustom) {
  return (
    courseDate.attributes.cardStackId !== null ||
    courseDate.attributes.unitIds.length > 0 ||
    courseDate.attributes.isForcedOn === true
  )
}

function verifyCourseDates(courseDates: Array<CourseCalendarDate>) {
  // make sure course starts have course ends
  // make sure all course ends are after or at course starts
  let unitMap: Record<string, { id: string; startDate: null | string; endDate: string | null }> = {}
  unitMap = pipe(
    courseDates,
    A.filter(isCourseDateCustom),
    A.sortBy((date) => date.attributes.dateString),
    A.reduce(unitMap, (acc, date) => {
      A.forEach(date.attributes.unitStart, (id) => {
        if (acc[id] !== undefined) {
          // "Multiple units with same id" in Sentry (using project fingerprint rule).
          throw Error(`Multiple units with the id: ${id}`)
        }
        acc[id] = { id: id, startDate: null, endDate: null }
        acc[id]["startDate"] = date.attributes.dateString
      })
      A.forEach(date.attributes.unitEnd, (id) => {
        if (acc[id] === undefined) {
          // "Unit start-end error" in Sentry (using project fingerprint rule).
          throw Error(`Unit end comes before unit beginning, ${id}, ${date.attributes.dateString}`)
        }
        acc[id]["endDate"] = date.attributes.dateString
      })
      return acc
    })
  )

  A.forEach(D.values(unitMap), (map) => {
    // The first two cases should be impossible with the current constructUnitsAndDates code.
    // However, end < start can happen if you reorder lessons.
    if (isNil(map.endDate) || isNil(map.startDate) || map.endDate < map.startDate) {
      console.log(`Problematic unit: ${map.id}`)
      console.log(map)
      // "Unit start-end error" in Sentry (using project fingerprint rule).
      throw Error("Unit starts don't match ends")
    }
  })
  // let unitIdsFromCourseDates = _.chain(courseDates).filter(cd => cd.type === "course-date-custom").flatMap(cd => cd.attributes.unitIds).sortBy().uniq().value()
  // let unitIdsFromUnits = _.sortBy(_.map(units, "id"))
  // let unitIdsFromMap   = _.sortBy(_.map(unitMap, "id"))
  //
  // _.forEach(unitIdsFromUnits, (id, index) => {
  //   if (unitIdsFromMap[index] !== id) throw Error("Unit array doesn't match the units in the dates", unitIdsFromMap, unitIdsFromUnits)
  //   if (unitIdsFromCourseDates[index] !== id) throw Error("Unit array doesn't match the units in the dates", unitIdsFromMap, unitIdsFromCourseDates)
  // })
}
